import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { Thread } from "@/entities/thread";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { getThreadLinkAttributes } from "@/features/catalog/hooks/useFilteredThreads";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import {
  type CatalogHoverLayout,
  computeCatalogHoverLayout,
  getCatalogHoverBody,
  resolveCatalogHoverImageSize,
  resolveCatalogHoverImageWidth,
} from "@/features/catalog/utils/catalogHoverPreview";
import { useIsDesktop } from "@/shared/hooks";

/** カードを素通りしただけでは表示しない、hover intentの待ち時間(ms)。 */
export const CATALOG_HOVER_PREVIEW_OPEN_DELAY_MS = 250;

/** ポップアップを閉じるまでの猶予(ms)。QuoteHoverPreviewと同値。 */
const CLOSE_DELAY_MS = 150;

interface Props {
  thread: Thread;
  /** カタログセルに表示中のサムネイルURL。ポップアップでも同じ画像を拡大する。 */
  imageUrl: string;
  /** ホバー検出の基準にするカタログセルのリンク要素。 */
  anchorRef: RefObject<HTMLElement | null>;
  /** 拡大画像を重ねる基準にするサムネイル要素。 */
  previewAnchorRef: RefObject<HTMLElement | null>;
  /** 元のカタログカードと同じクリック処理（閲覧履歴記録等）。 */
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  /** 元のカタログカードと同じタブで開くか、新規タブで開くか。 */
  openInNewTab: boolean;
}

/**
 * カタログカードのホバーでスレ画の拡大とOP本文を表示する。
 *
 * - 呼び出し側(DesktopCatalogItem)が設定catalogHoverPreviewを見て条件付きでマウントする。
 *   マウントされない限りイベントリスナーは一切登録されない。
 * - hoverの概念がないタッチデバイスを避けるため、QuoteHoverPreviewと同じく
 *   useIsDesktop()でモバイルを除外する。
 * - 本文は切り詰めず、ポップアップ内でスクロールさせる(追加フェッチは不要。
 *   データはカタログのthread.opPostにすべて含まれる)。
 */
export const CatalogHoverPreview: React.FunctionComponent<Props> = ({
  thread,
  ...props
}: Props) => {
  const isDesktop = useIsDesktop();
  const scale = useCatalogStore((state) => state.catalogHoverScale);
  return (
    <CatalogHoverPreviewView
      {...props}
      thread={thread}
      isDesktop={isDesktop}
      scale={scale}
      body={getCatalogHoverBody(thread)}
    />
  );
};

export type CatalogHoverPreviewViewProps = Props & {
  readonly isDesktop: boolean;
  readonly scale: number;
  readonly body: string;
};

export const CatalogHoverPreviewView: React.FunctionComponent<
  CatalogHoverPreviewViewProps
> = ({
  thread,
  imageUrl,
  anchorRef,
  previewAnchorRef,
  onClick,
  openInNewTab,
  isDesktop,
  scale,
  body,
}: CatalogHoverPreviewViewProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [layout, setLayout] = useState<CatalogHoverLayout | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const bodyRef = useRef<HTMLAnchorElement>(null);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const cancelOpen = useCallback((): void => {
    if (openTimerRef.current === null) return;
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = null;
  }, []);

  const cancelClose = useCallback((): void => {
    if (closeTimerRef.current === null) return;
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  }, []);

  const scheduleClose = useCallback((): void => {
    cancelOpen();
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setIsActive(false);
      setIsOpen(false);
    }, CLOSE_DELAY_MS);
  }, [cancelClose, cancelOpen]);

  const openImmediately = useCallback((): void => {
    cancelOpen();
    cancelClose();
    setIsOpen(true);
  }, [cancelClose, cancelOpen]);

  const scheduleOpen = useCallback((): void => {
    cancelOpen();
    cancelClose();
    openTimerRef.current = window.setTimeout(() => {
      openTimerRef.current = null;
      setIsOpen(true);
    }, CATALOG_HOVER_PREVIEW_OPEN_DELAY_MS);
  }, [cancelClose, cancelOpen]);

  const updatePosition = useCallback((): void => {
    const anchor = previewAnchorRef.current;
    const image = imageRef.current;
    if (!anchor || !image) return;
    const imageSize = resolveCatalogHoverImageSize({
      complete: image.complete,
      naturalWidth: image.naturalWidth,
      naturalHeight: image.naturalHeight,
      offsetWidth: image.offsetWidth,
      offsetHeight: image.offsetHeight,
    });
    if (!imageSize) {
      setLayout(null);
      return;
    }
    const anchorRect = anchor.getBoundingClientRect();
    const bodyElement = bodyRef.current;
    setLayout(
      computeCatalogHoverLayout(
        anchorRect,
        imageSize,
        bodyElement
          ? { width: bodyElement.offsetWidth, height: bodyElement.offsetHeight }
          : null,
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
  }, [previewAnchorRef]);

  // 無効時(未マウント)はここに到達しない。有効時だけカードへリスナーを張る。
  useEffect(() => {
    if (!isDesktop) return;
    const anchor = anchorRef.current;
    if (!anchor) return;

    anchor.addEventListener("mouseenter", scheduleOpen);
    anchor.addEventListener("mouseleave", scheduleClose);
    anchor.addEventListener("focus", openImmediately);
    anchor.addEventListener("blur", scheduleClose);
    return (): void => {
      anchor.removeEventListener("mouseenter", scheduleOpen);
      anchor.removeEventListener("mouseleave", scheduleClose);
      anchor.removeEventListener("focus", openImmediately);
      anchor.removeEventListener("blur", scheduleClose);
    };
  }, [anchorRef, isDesktop, openImmediately, scheduleClose, scheduleOpen]);

  useEffect(
    () => (): void => {
      cancelOpen();
      cancelClose();
    },
    [cancelClose, cancelOpen],
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      setLayout(null);
      return;
    }
    updatePosition();
    const animationFrame = window.requestAnimationFrame(() => {
      setIsActive(true);
    });
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return (): void => {
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  if (!isDesktop || !isOpen || typeof document === "undefined") return null;

  return createPortal(
    <CatalogHoverPreviewPopup
      thread={thread}
      imageUrl={imageUrl}
      imageWidth={resolveCatalogHoverImageWidth(scale)}
      body={body}
      layout={layout}
      isActive={isActive}
      imageRef={imageRef}
      bodyRef={bodyRef}
      onClick={onClick}
      openInNewTab={openInNewTab}
      onImageLoad={updatePosition}
      onInteractionStart={cancelClose}
      onInteractionEnd={scheduleClose}
    />,
    document.body,
  );
};

interface CatalogHoverPreviewPopupProps {
  thread: Thread;
  imageUrl: string;
  imageWidth: number;
  body: string;
  layout: CatalogHoverLayout | null;
  isActive: boolean;
  imageRef?: RefObject<HTMLImageElement | null>;
  bodyRef?: RefObject<HTMLAnchorElement | null>;
  onClick: React.MouseEventHandler<HTMLAnchorElement>;
  openInNewTab: boolean;
  onImageLoad: React.ReactEventHandler<HTMLImageElement>;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
}

/** 端末判定や表示ライフサイクルから独立した、画像・本文リンクの描画部分。 */
export function CatalogHoverPreviewPopup({
  thread,
  imageUrl,
  imageWidth,
  body,
  layout,
  isActive,
  imageRef,
  bodyRef,
  onClick,
  openInNewTab,
  onImageLoad,
  onInteractionStart,
  onInteractionEnd,
}: CatalogHoverPreviewPopupProps): React.ReactElement {
  const threadLinkAttributes = getThreadLinkAttributes(openInNewTab);

  return (
    <aside
      aria-label="スレッドプレビュー"
      className={`catalog-hover-popup${isActive ? " is-active" : ""}`}
      style={{
        // 位置確定前の一瞬だけ隠す(初期表示位置のちらつき防止)
        visibility: layout ? undefined : "hidden",
      }}
    >
      <CatalogThreadLink
        thread={thread}
        className="catalog-hover-popup-link catalog-hover-popup-image-link"
        aria-label="プレビュー画像からスレッドを開く"
        onClick={onClick}
        onMouseEnter={onInteractionStart}
        onMouseLeave={onInteractionEnd}
        onFocus={onInteractionStart}
        onBlur={onInteractionEnd}
        {...threadLinkAttributes}
        style={{
          left: layout?.image.left ?? 0,
          top: layout?.image.top ?? 0,
        }}
      >
        <img
          ref={imageRef}
          className="catalog-hover-popup-image"
          src={imageUrl}
          alt=""
          aria-hidden="true"
          style={{ width: imageWidth }}
          onLoad={onImageLoad}
          decoding="async"
        />
      </CatalogThreadLink>
      {/* 本文がある場合は常に画像と一緒に表示する。 */}
      {body && (
        <CatalogThreadLink
          ref={bodyRef}
          thread={thread}
          className="catalog-hover-popup-link catalog-hover-popup-body"
          onClick={onClick}
          onMouseEnter={onInteractionStart}
          onMouseLeave={onInteractionEnd}
          onFocus={onInteractionStart}
          onBlur={onInteractionEnd}
          {...threadLinkAttributes}
          style={{
            left: layout?.body?.left ?? 0,
            top: layout?.body?.top ?? 0,
          }}
        >
          {body}
        </CatalogThreadLink>
      )}
    </aside>
  );
}
